import { GridFilterModel, GridSortModel } from "@mui/x-data-grid-premium";

/**
 * Manages URL state synchronization for DataGrid filter and sort models.
 *
 * URL Format:
 * - Filters: {tableName}_filter_{field}_operator={operator}&{tableName}_filter_{field}_value={value}
 * - Sorting: {tableName}_sort_{field}={direction}
 * - Search: {tableName}_search={value}
 * - Row Grouping: {tableName}_group={field1},{field2},... (comma-separated, preserves order)
 *
 * Example:
 * ?products_filter_name_operator=contains&products_filter_name_value=iPhone
 * &products_filter_price_operator=greaterThan&products_filter_price_value=500
 * &products_sort_name=asc
 * &products_search=laptop
 * &products_group=category,brand
 */
// Operators that don't require a value (unary operators)
const VALUELESS_OPERATORS = new Set(["isEmpty", "isNotEmpty"]);

export class DataGridUrlStateManager {
  private tableName: string;
  private filterPrefix: string;
  private sortPrefix: string;
  private searchKey: string;
  private rowGroupingKey: string;

  constructor(tableName: string) {
    this.tableName = tableName;
    this.filterPrefix = `${tableName}_filter_`;
    this.sortPrefix = `${tableName}_sort_`;
    this.searchKey = `${tableName}_search`;
    this.rowGroupingKey = `${tableName}_group`;
  }

  /**
   * Reads the current URL and extracts the filter model for this table.
   */
  public readFilterModelFromUrl(): GridFilterModel | undefined {
    const params = new URLSearchParams(window.location.search);
    const filterItems: Array<{
      field: string;
      operator: string;
      value: any;
    }> = [];

    // Group parameters by field
    const fieldMap = new Map<string, { operator?: string; value?: any }>();

    params.forEach((value, key) => {
      if (key.startsWith(this.filterPrefix)) {
        const remainder = key.substring(this.filterPrefix.length);
        const lastUnderscoreIndex = remainder.lastIndexOf("_");

        if (lastUnderscoreIndex === -1) return;

        const field = remainder.substring(0, lastUnderscoreIndex);
        const type = remainder.substring(lastUnderscoreIndex + 1); // "operator" or "value"

        if (!fieldMap.has(field)) {
          fieldMap.set(field, {});
        }

        const fieldData = fieldMap.get(field)!;
        if (type === "operator") {
          fieldData.operator = value;
        } else if (type === "value") {
          // Try to parse as number if it looks like a number
          fieldData.value = this.parseValue(value);
        }
      }
    });

    // Convert map to filter items
    fieldMap.forEach((data, field) => {
      if (data.operator) {
        // Include filter if it has a value OR if it's a valueless operator (isEmpty, isNotEmpty)
        if (
          data.value !== undefined ||
          VALUELESS_OPERATORS.has(data.operator)
        ) {
          filterItems.push({
            field,
            operator: data.operator,
            value: data.value,
          });
        }
      }
    });

    if (filterItems.length === 0) {
      return undefined;
    }

    return {
      items: filterItems,
    };
  }

  /**
   * Reads the current URL and extracts the sort model for this table.
   */
  public readSortModelFromUrl(): GridSortModel | undefined {
    const params = new URLSearchParams(window.location.search);
    const sortItems: Array<{ field: string; sort: "asc" | "desc" }> = [];

    params.forEach((value, key) => {
      if (key.startsWith(this.sortPrefix)) {
        const field = key.substring(this.sortPrefix.length);
        if (value === "asc" || value === "desc") {
          sortItems.push({ field, sort: value });
        }
      }
    });

    return sortItems.length > 0 ? sortItems : undefined;
  }

  /**
   * Updates the URL with the current filter model, preserving other query parameters.
   */
  public writeFilterModelToUrl(filterModel: GridFilterModel | undefined): void {
    const params = new URLSearchParams(window.location.search);

    // Remove all existing filter parameters for this table
    const keysToDelete: string[] = [];
    params.forEach((_, key) => {
      if (key.startsWith(this.filterPrefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => params.delete(key));

    // Add new filter parameters
    if (filterModel && filterModel.items && filterModel.items.length > 0) {
      filterModel.items.forEach((item) => {
        if (!item.field || !item.operator) return;

        // Check if this operator requires a value
        const isValuelessOperator = VALUELESS_OPERATORS.has(item.operator);

        // Include filter if it has a value OR if it's a valueless operator
        if (item.value !== undefined || isValuelessOperator) {
          params.set(
            `${this.filterPrefix}${item.field}_operator`,
            item.operator
          );
          // Only set value if it exists (valueless operators won't have one)
          if (item.value !== undefined) {
            params.set(
              `${this.filterPrefix}${item.field}_value`,
              String(item.value)
            );
          }
        }
      });
    }

    this.updateUrl(params);
  }

  /**
   * Updates the URL with the current sort model, preserving other query parameters.
   */
  public writeSortModelToUrl(sortModel: GridSortModel | undefined): void {
    const params = new URLSearchParams(window.location.search);

    // Remove all existing sort parameters for this table
    const keysToDelete: string[] = [];
    params.forEach((_, key) => {
      if (key.startsWith(this.sortPrefix)) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => params.delete(key));

    // Add new sort parameters
    if (sortModel && sortModel.length > 0) {
      sortModel.forEach((item) => {
        if (item.field && item.sort) {
          params.set(`${this.sortPrefix}${item.field}`, item.sort);
        }
      });
    }

    this.updateUrl(params);
  }

  /**
   * Updates the URL with the current search value, preserving other query parameters.
   */
  public writeSearchToUrl(searchValue: string | undefined): void {
    const params = new URLSearchParams(window.location.search);

    // Trim the search value to handle whitespace
    const trimmedValue = searchValue?.trim();

    if (trimmedValue) {
      params.set(this.searchKey, trimmedValue);
    } else {
      params.delete(this.searchKey);
    }

    this.updateUrl(params);
  }

  /**
   * Reads the current URL and extracts the search value for this table.
   */
  public readSearchFromUrl(): string | undefined {
    const params = new URLSearchParams(window.location.search);
    return params.get(this.searchKey) || undefined;
  }

  /**
   * Reads the current URL and extracts the row grouping model for this table.
   * Returns an array of field names in order, or undefined if not present.
   */
  public readRowGroupingModelFromUrl(): string[] | undefined {
    const params = new URLSearchParams(window.location.search);
    const groupValue = params.get(this.rowGroupingKey);

    if (!groupValue) {
      return undefined;
    }

    // Split by comma and filter out empty strings
    const fields = groupValue.split(",").filter((field) => field.trim() !== "");
    return fields.length > 0 ? fields : undefined;
  }

  /**
   * Updates the URL with the current row grouping model, preserving other query parameters.
   */
  public writeRowGroupingModelToUrl(
    rowGroupingModel: string[] | undefined
  ): void {
    const params = new URLSearchParams(window.location.search);

    if (rowGroupingModel && rowGroupingModel.length > 0) {
      // Join fields with comma to preserve order
      params.set(this.rowGroupingKey, rowGroupingModel.join(","));
    } else {
      params.delete(this.rowGroupingKey);
    }

    this.updateUrl(params);
  }

  /**
   * Clears all filter, sort, and row grouping parameters for this table from the URL.
   */
  public clearUrlState(): void {
    const params = new URLSearchParams(window.location.search);

    const keysToDelete: string[] = [];
    params.forEach((_, key) => {
      if (
        key.startsWith(this.filterPrefix) ||
        key.startsWith(this.sortPrefix) ||
        key === this.rowGroupingKey
      ) {
        keysToDelete.push(key);
      }
    });
    keysToDelete.forEach((key) => params.delete(key));

    this.updateUrl(params);
  }

  /**
   * Updates the browser URL without triggering a page reload.
   */
  private updateUrl(params: URLSearchParams): void {
    const newUrl = params.toString()
      ? `${window.location.pathname}?${params.toString()}`
      : window.location.pathname;

    window.history.replaceState({}, "", newUrl);
  }

  /**
   * Attempts to parse a string value into its appropriate type.
   */
  private parseValue(value: string): any {
    // Try to parse as boolean first
    if (value.toLowerCase() === "true") return true;
    if (value.toLowerCase() === "false") return false;

    // Don't convert to number if it has leading zeros (like UPCs)
    // or if it would change the string representation
    const trimmedValue = value.trim();
    if (!isNaN(Number(trimmedValue)) && trimmedValue !== "") {
      const numValue = Number(trimmedValue);
      // Only convert to number if string representation matches
      // This preserves leading zeros in UPCs like "000768503928"
      if (numValue.toString() === trimmedValue) {
        return numValue;
      }
    }

    // Return as string (preserves leading zeros, etc.)
    return value;
  }
}
